<template>
    <div>
        <modal v-model="helpModalOpen">
            <span slot="title">Help</span>
            <h2><em>{{ transformNameToDisplayName(helpField) }}</em></h2>
            <div class="alert alert-info">
                ISO 11179 help definition
            </div>
            <div class="content-body">
                {{ getHelpText(helpField) }}
            </div>
            <span slot="footer">
                <button class="btn btn-outline-info" @click="helpModalOpen=false">
                    <i class="fa fa-times" aria-hidden="true" /> Close
                </button>
            </span>
        </modal>
        <api-errors :errors="getErrors" />
        <loading v-if="loading" />
        <div v-else-if="ready" id="inventory">
            <div class="heading">
        <div class="row">
                    <div class="col-lg-12 clearfix">
                        <a href="#inventory">Show table & headers</a>
                        <div class="pull-right">
                            <a :href="downloadCSVUrl">
                                <button :disabled="!tableDataExists" class="btn btn-default">
                                    <i class="fas fa-file-csv" />
                                    Download CSV
                                </button>
                            </a>
                            <a :href="downloadExcelUrl">
                                <button :disabled="!tableDataExists" class="btn btn-default">
                                    <i class="fas fa-file-excel" />
                                    Download Excel File
                                </button>
                            </a>
                            <span class="btn-group" style="margin-left: 10px;">
                                <a :href="cardViewUrl" class="btn btn-default">
                                    <i class="glyphicon glyphicon-th-large" /> Card View
                                </a>
                                <button class="btn btn-primary" disabled>
                                    <i class="glyphicon glyphicon-th-list" /> List View
                                </button>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div style="flex-grow:1; overflow:hidden; display:flex">
                <ve-table
                    :columns="veColumns"
                    :table-data="veTableData"
                    :expand-option="expandOption"
                    row-key-field-name="rowKey"
                    :fixed-header="true"
                    :border-around="true"
                    :border-x="true"
                    :border-y="true"
                    :scroll-width="0"
                    :row-style-option="rowStyleOption"
                    :column-width-resize-option="columnWidthResizeOption"
                    max-height="100%"
                    max-width="100%"
                />
            </div>
            <div class="table-pagination">
                <a href="#inventory" class="pull-left">Show table & headers</a>
                <ve-pagination
                    :layout="['total', 'prev', 'pager', 'next', 'jumper']"
                    :total="totalCount"
                    :page-index="pageIndex"
                    :page-size="pageSize"
                    @on-page-number-change="pageNumberChange"
                />
            </div>
        </div>
    </div>
</template>

<script>
import Loading from 'src/components/Loading.vue'
import apiRequest from 'src/mixins/api-request.js'
import apiErrors from '@/ApiErrorDisplay.vue'
import { VeTable, VePagination } from 'vue-easytable'
import "vue-easytable/libs/theme-default/index.css"
import {v4 as generateUUID} from 'uuid'
import Modal from 'uiv/src/components/modal/Modal.vue'
import { reverse } from 'src/lib/data/urls'

export default {
    name: "Inventory",
    components: {
        'loading': Loading,
        'api-errors': apiErrors,
        've-table': VeTable,
        've-pagination': VePagination,
        'modal': Modal,
    },
    mixins: [apiRequest],
    props: {
        inventoryUuid: {
            type: String,
            default: "",
        },
        displayNameHelpMapping: {
            type: String,
            required: true,
        },
        stewardshipSlug: {
        type: String,
        required: false
        },
        inStewardshipContext: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            expandOption: {
                trigger: "cell",
                render: ({ row }, h) => {
                    const columnsToRender = this.tableData.columns.slice(1, -1)
                    return h("table", { style: "max-width: 95vw; background-color: #F5F5F5;" },
                        columnsToRender.map(column => {
                            const name = column
                            const value = row[name]
                            return h("tr", { style: "border-bottom: 1px solid #ddd;" }, [
                                h("th", { style: "width: 10%; padding: 10px; text-align: left; vertical-align: top;" }, this.transformNameToDisplayName(name)),
                                h("td", { style: "width: 90%; padding: 10px; text-align: left;", domProps: { innerHTML: value } })
                            ])
                        })
                    )
                },
            },
            ready: false,
            loading: true,
            helpModalOpen: false,
            helpField:'',
            showingHelp: true,
            inventoryItemsEndpoint: "",
            tableData: {},
            tableDataForDownload: {},
            getErrors: {},
            veColumns: [],
            veTableData: [],
            rowStyleOption: {
                stripe: true,
            },
            totalCount: 0,
            pageIndex: 1,
            pageSize: 20,
            columnWidthResizeOption: {
                // default false
                enable: true,
                // column resize min width
                minWidth: 30,
            },
            columnResizeInfo: {
                column: "",
                differWidth: "",
                columnWidth: "",
                tableWidth: "",
            },
            sortColumn: "",
            sortDirection: "",
            filterColumn: "",
            filterValue: "",
            filterColumnQuery: "",
            filterValueQuery: "",
            downloadCSVUrl: "",
            downloadExcelUrl: "",
            jsonParseDisplayNameHelpMapping: {},
            fieldDisplayNameHelpMap: new Map(),
        }
    },
    computed: {
        cardViewUrl() {
            if (this.inStewardshipContext && this.stewardshipSlug) {
                return reverse('stewardship_inventory_card', [this.stewardshipSlug, this.inventoryUuid]);
            } else {
                return reverse('inventory_card', [this.inventoryUuid]);
            }
        },
        tableDataExists: function() {
            return (Object.keys(this.tableData.inventory_items).length > 0)
        },
    },
    mounted() {
        this.jsonParseDisplayNameHelpMapping = JSON.parse(this.displayNameHelpMapping)
        this.inventoryItemsEndpoint = "/inventory/api/" + this.inventoryUuid + "/items/"
        this.getTableData()
        this.downloadCSVUrl = "/inventory/api/download/" + this.inventoryUuid + "/?type=csv"
        this.downloadExcelUrl = "/inventory/api/download/" + this.inventoryUuid + "/?type=xlsx"
        this.fieldDisplayNameHelpMap = this.getNameToDisplayNameHelpMap()
    },
    methods: {
        formatDateValue(key, value) {
            if (!value) return '';
            const isDateField = key === 'created' ||
                            key === 'modified' ||
                            key.toLowerCase().includes('date');

            if (isDateField && typeof value === 'string') {
                if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
                    try {
                        const date = new Date(value);
                        if (!isNaN(date.getTime())) {
                            return new Intl.DateTimeFormat(navigator.language).format(date).replace(/\//g, '-');
                        }
                    } catch (e) {
                        console.warn('Date formatting error:', e);
                    }
                }
            }
            return value;
        },
        getTableData() {
            let tableDataPromise = this.get(this.inventoryItemsEndpoint)
            tableDataPromise.then((response => {
                this.tableData = response.data.results
                this.totalCount = response.data.count
                this.veColumns = this.transformToVeColumns(this.tableData.columns)
                this.veTableData = this.transformToVeTableData(this.tableData.inventory_items)
                this.ready = true
                this.loading = false
            })).catch((error) => {
                this.getErrors = error.response.data
            })
        },
        getNameToDisplayNameHelpMap () {
            let map = new Map()
            let allFields = [...this.jsonParseDisplayNameHelpMapping.fields, ...this.jsonParseDisplayNameHelpMapping.custom_fields, ...this.jsonParseDisplayNameHelpMapping.alternative_name_types, ...this.jsonParseDisplayNameHelpMapping.namespaces]
            allFields.forEach(obj => {
                map.set(obj.value, {displayName: obj.name, helpText: obj.help_text})
            })
            return map
        },
        getHelpText(field) {
            if (this.fieldDisplayNameHelpMap.has(field)) {
                return this.fieldDisplayNameHelpMap.get(field).helpText
            } else {
                return ''
            }
        },
        transformNameToDisplayName (field) {
            if (this.fieldDisplayNameHelpMap.has(field)) {
                return this.fieldDisplayNameHelpMap.get(field).displayName
            } else {
                return field.charAt(0).toUpperCase() + field.slice(1)
            }
        },
        transformToVeColumns(columns) {
            columns.shift()
            columns.unshift("expand")
            const transformedColumns = columns.map((field) => {
                const column = {
                    field,
                    key: generateUUID(),
                    title: this.transformNameToDisplayName(field),
                    align: "left",
                    width: 220,
                    ellipsis: {
                            showTitle: false,
                    },
                }
                // Add a custom render function for all columns
                column.renderBodyCell = ({ row }, h) => {
                    const formattedValue = this.formatDateValue(field, row[field]);
                        // Use v-html to render HTML content
                        return h("div", {domProps: {innerHTML: formattedValue}})
                }
                if (field === "expand") {
                    column.width = 50
                    column.title = ""
                    column.type = "expand"
                    column.align = "center"
                }
                if (field === "uuid" || field === "created" || field === "modified") {
                    delete column.ellipsis
                }
                if (field !== "absolute_url" && field !== "edit_url" && field !== "expand") {
                        column.renderHeaderCell = ({ column }, h) => {
                        const field = column.field
                        const openHelpModal = () => {
                            this.helpModalOpen = true
                            this.helpField = field
                        }
                        const handleSort = (field, direction) => {
                            this.sortColumn = field
                            this.sortDirection = direction
                            this.pageIndex = 1
                            this.filterColumnQuery = ""
                            this.filterValueQuery = ""
                            this.inventoryItemsEndpoint = "/inventory/api/" + this.inventoryUuid + "/items/" + `?page=${this.pageIndex}&sort=${this.sortColumn}&order=${this.sortDirection}`
                            this.getTableData()
                        }

                        return h("div", { style: { display: "flex", alignItems: "center" } }, [
                                h("span", {
                                    style: {
                                    fontWeight: "bold",
                                    },
                                }, column.title),
                                h("i", {
                                    class: "fa fa-question-circle",
                                    style: {
                                    color: "#337AB7",
                                    marginLeft: "5px",
                                    cursor: "pointer",
                                    fontSize: "1.2rem",
                                    },
                                    on: {
                                    click: openHelpModal,
                                    },
                                }),
                                h("i", {
                                    class: "fas fa-sort-up",
                                    style: {
                                    color: this.sortColumn === field && this.sortDirection === "asc" ? "#337AB7" : "",
                                    cursor: "pointer",
                                    fontSize: "1.5rem",
                                    marginLeft: "5px",
                                    },
                                    on: {
                                    click: () => {
                                        handleSort(field, "asc");
                                    },
                                    },
                                }),
                                h("i", {
                                    class: "fas fa-sort-down",
                                    style: {
                                    color: this.sortColumn === field && this.sortDirection === "desc" ? "#337AB7" : "",
                                    cursor: "pointer",
                                    fontSize: "1.5rem",
                                    marginLeft: "5px",
                                    },
                                    on: {
                                    click: () => {
                                        handleSort(field, "desc");
                                    },
                                    },
                                }),
                            ])
                        }
                // filter custom
                    column.filterCustom = {

                        defaultVisible: false,
                        render: ({ closeFn }, h) => {
                        const field = column.field

                        const filterValue = this.filterValue

                        const onInput = (e) => {
                            this.filterValue = e.target.value
                        }

                        const searchCancel = () => {
                            this.filterValue = '' // Clear the search value
                            closeFn()
                        }

                        const searchConfirm = () => {
                            this.filterColumn = field
                            this.filterValue = filterValue
                            this.filterColumnQuery = this.filterColumn
                            this.filterValueQuery = this.filterValue
                            this.pageIndex = 1
                            this.sortColumn = ""
                            this.sortDirection = ""
                            this.inventoryItemsEndpoint = `/inventory/api/${this.inventoryUuid}/items/?page=${this.pageIndex}&filter=${this.filterColumnQuery}&value=${this.filterValueQuery}`
                            this.getTableData()
                            this.filterColumn = ''
                            this.filterValue = ''
                            closeFn()
                        }

                            return h(
                                'div',
                                { class: 'custom-name-filter' },
                                [
                                h('input', {
                                    attrs: {
                                    value: filterValue,
                                    placeholder: 'Search...',
                                    },
                                    on: {
                                    input: onInput,
                                    },
                                }),
                                h(
                                    'div',
                                    { class: 'custom-name-filter-operation' },
                                    [
                                    h(
                                        'span',
                                        {
                                        class: 'name-filter-cancel',
                                        on: {
                                            click: searchCancel,
                                        },
                                        },
                                        'Cancel'
                                    ),
                                    h(
                                        'span',
                                        {
                                        class: 'name-filter-confirm',
                                        on: {
                                            click: searchConfirm,
                                        },
                                        },
                                        'Filter'
                                    ),
                                    ]
                                ),
                                ]
                            )
                            },
                        }
                }
                if (field === "name") {
                    column.renderBodyCell = ({ row }, h) => {
                        const text = row.absolute_url
                            const link = h(
                            "a",
                            {
                                attrs: {
                                    href: text,
                                    style: "color:#337AB7;cursor:pointer;",
                                    target: "_blank",
                                },
                            },
                            [row.name]
                            )
                            return h(
                                "span",
                                [link]
                            )
                        }
                }
                if (field === "edit_url") {
                    column.title = "Edit Item"
                    column.renderBodyCell = ({ row, column }, h) => {
                        if (row[column.field] === "-") {
                            return h("div", "-")
                        } else {
                            const text = row[column.field]
                            const link = h(
                                "a",
                                {
                                    attrs: {
                                        href: text,
                                        style: "color:#337AB7;cursor:pointer;",
                                        target: "_blank",
                                    },
                                },
                                ["Edit"]
                                )
                                return h(
                                    "span",
                                    [link]
                            )
                        }
                    }
                    column.renderHeaderCell = ({ column }, h) => {
                    return h("div", [
                        h("span", {
                            style: {
                            fontWeight: "bold",
                            },
                        }, column.title)

                        ])
                        }
                    }
            return column
            })
            return transformedColumns
        },
        transformToVeTableData(inventory_items) {
            const result = inventory_items.map(item => {
                const cv_columns = {}
                const custom_values = item.custom_values || []

                custom_values.forEach(({ system_name, content }) => {
                    cv_columns[system_name] = this.formatDateValue(system_name, content)
                })

                const alt_name_columns = {}
                const alternative_names = item.alternative_names || []
                alternative_names.forEach(({ alternative_name_type_title, value }) => {
                    alt_name_columns[alternative_name_type_title] = this.formatDateValue(alternative_name_type_title, value)
                })

                const identifiers_columns = item.identifiers.reduce((acc, curr) => {
                    const key = curr.namespace_name
                    const value = `${curr.identifier}/${curr.version}`
                    if (!acc[key]) {
                        acc[key] = value;
                    } else {
                        acc[key] += `, ${value}`
                    }
                    return acc
                }, {})

                const currentStatusesString = item.current_statuses.map(status =>
                    `${status.registration_authority_name}:${status.state_name}`).join(', ')

                delete item.custom_values
                delete item.alternative_names
                delete item.identifiers

                const formattedItem = {}
                Object.keys(item).forEach(key => {
                    formattedItem[key] = this.formatDateValue(key, item[key])
                })

                const rowKey = generateUUID()
                return {
                    rowKey,
                    ...formattedItem,
                    ...cv_columns,
                    ...alt_name_columns,
                    ...identifiers_columns,
                    current_statuses: currentStatusesString,
                }
            })
            return result
            },
        // page number change
        pageNumberChange(pageIndex) {
            this.pageIndex = pageIndex
            this.inventoryItemsEndpoint = "/inventory/api/" + this.inventoryUuid + "/items/" + "?page=" + pageIndex + `&sort=${this.sortColumn}&order=${this.sortDirection}` + `&filter=${this.filterColumnQuery}&value=${this.filterValueQuery}`
            this.getTableData()

            // Force view to include the full inventory view
            document.getElementById('inventory').scrollIntoView();
        },
    }
}
</script>

<style>
.table-pagination {
    margin-top: 20px;
    text-align: right;
}
    /* Override the default style */
dl dt:after {
content: none;
}
.custom-name-filter {
  padding: 10px;
}

.custom-name-filter-operation {
  cursor: pointer;
  margin: 10px 10px 0px 0;
}

.name-filter-cancel:hover,
.name-filter-confirm:hover {
  color: #1890ff;
}

.name-filter-cancel {
  float: left;
}

.name-filter-confirm {
  float: right;
}

.heading {
    background-color: #f5f5f5;
    border-color: #dddddd;
    padding: 10px 15px;
    border-top: 1px solid #dddddd;
    border-bottom: 1px solid #dddddd;
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;
}

#inventory {
    min-height:300px;
    max-height:100vh;
    display:flex;
    flex-direction:column;
}

#inventory .ve-table.ve-table-border-around {
    height: 100%;
}

#inventory .vue-table-root {
    max-height: 100%;
    max-width: 100%;
}

*:target {
    /* This overrides and prevents an internal css style */
    background-color:unset
}
.ve-table .ve-table-container .ve-table-content-wrapper table.ve-table-content tbody.ve-table-body .ve-table-expand-tr .ve-table-expand-td-content {
    padding: 0;
}
</style>
